import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { LanguageCode } from '../types/language';
import { DEFAULT_LANG, AVAILABLE_LANGUAGES, translations } from '../config/translations';
import { localStorageUtils } from '../utils/localStorage';
import { createLogger } from '../utils/logger';

const logger = createLogger('LanguageContext');
const LANGUAGE_STORAGE_KEY = 'language';

interface LanguageContextType {
    language: LanguageCode;
    setLanguage: (lang: LanguageCode) => void;
    t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [language, setLanguageState] = useState<LanguageCode>(() => {
        const stored = localStorageUtils.getItem(LANGUAGE_STORAGE_KEY);
        if (stored && AVAILABLE_LANGUAGES.includes(stored)) {
            return stored as LanguageCode;
        }
        return DEFAULT_LANG;
    });

    const setLanguage = (lang: LanguageCode) => {
        if (AVAILABLE_LANGUAGES.includes(lang)) {
            setLanguageState(lang);
            localStorageUtils.setItem(LANGUAGE_STORAGE_KEY, lang);
        }
    };

    const t = (key: string): string => {
        const keys = key.split('.');
        let value: any = translations[language];

        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                logger.warn('Translation key not found:', key);
                return key;
            }
        }

        return typeof value === 'string' ? value : key;
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage(): LanguageContextType {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}
